/** Canvas Kingdom — Phase A entry point.
 *  Reference: docs/CLAUDE_CODE_UI_IMPLEMENTATION_BRIEF.md §7
 *  - 3 screens(TOP / Home / Gameplay)を ScreenManager で切替
 *  - TOP → Home → Gameplay の遷移、Gameplay の歯車で Home に戻る
 *  - 既存 Pixi コアロジックは GameplayScreen に集約
 */

import { ScreenManager } from './ui/ScreenManager';
import { TopScreen } from './ui/screens/TopScreen';
import { HomeScreen } from './ui/screens/HomeScreen';
import { GameplayScreen, CONTINUE_COLOR_COST } from './ui/screens/GameplayScreen';
import { ShopScreen } from './ui/screens/ShopScreen';
import { CharacterScreen } from './ui/screens/CharacterScreen';
import { StageLoader } from './game/StageLoader';
import { HeartsStore } from './game/HeartsStore';
import { InventoryStore, HELPER_META } from './game/InventoryStore';
import { BoosterStore, BOOSTER_META } from './game/BoosterStore';
import { CharacterStore, CHARACTER_META } from './game/CharacterStore';
import { AdMockOverlay } from './ui/AdMockOverlay';
import { PaintingFragmentStore } from './game/PaintingFragmentStore';
import { SoundManager } from './audio/SoundManager';
import { StreakStore } from './game/StreakStore';
import { DailyQuestStore } from './game/DailyQuestStore';
import { DailyChallengeStore } from './game/DailyChallengeStore';
import { AchievementStore, ACHIEVEMENT_META } from './game/AchievementStore';
import { SettingsStore } from './game/SettingsStore';
import { PaintingGalleryScreen } from './ui/screens/PaintingGalleryScreen';

// localStorage キー(進行状態の永続化)
const LS_CURRENT_STAGE = 'ck.currentStage';
const LS_CLEARED_STAGES = 'ck.clearedStages';
const LS_COLOR_BALANCE = 'ck.colorBalance';
const LS_LAST_LOGIN = 'ck.lastLogin'; // ISO date 'YYYY-MM-DD'
const FIRST_STAGE_ID = '1-01';
const INITIAL_COLOR_BALANCE = 800;
const DAILY_LOGIN_BONUS = 30;

function loadCurrentStageId(): string {
  try {
    const v = window.localStorage.getItem(LS_CURRENT_STAGE);
    if (v && v.length > 0) return v;
  } catch {
    /* localStorage が使えない環境(プライベートモード等)は無視 */
  }
  return FIRST_STAGE_ID;
}

function saveCurrentStageId(id: string): void {
  try {
    window.localStorage.setItem(LS_CURRENT_STAGE, id);
  } catch {
    /* noop */
  }
}

function loadClearedStages(): Set<string> {
  try {
    const raw = window.localStorage.getItem(LS_CLEARED_STAGES);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((v): v is string => typeof v === 'string'));
      }
    }
  } catch {
    /* noop */
  }
  return new Set<string>();
}

function saveClearedStages(s: Set<string>): void {
  try {
    window.localStorage.setItem(LS_CLEARED_STAGES, JSON.stringify(Array.from(s)));
  } catch {
    /* noop */
  }
}

function loadColorBalance(): number {
  try {
    const v = window.localStorage.getItem(LS_COLOR_BALANCE);
    if (v) {
      const n = parseInt(v, 10);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  } catch {
    /* noop */
  }
  return INITIAL_COLOR_BALANCE;
}

function saveColorBalance(n: number): void {
  try {
    window.localStorage.setItem(LS_COLOR_BALANCE, String(Math.max(0, Math.floor(n))));
  } catch {
    /* noop */
  }
}

/** stageOrder の次のステージ id を返す。最後ならループ最初に戻る。 */
function nextStageId(current: string, order: readonly string[]): string {
  const idx = order.indexOf(current);
  if (idx === -1) return order[0] ?? FIRST_STAGE_ID;
  return order[(idx + 1) % order.length] ?? FIRST_STAGE_ID;
}

/** ステージクリア時のヘルパー獲得通知:画面上部にチップで一瞬表示。 */
function showHelperGrantToast(icon: string, name: string): void {
  let toast = document.querySelector<HTMLElement>('.helper-grant-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'helper-grant-toast';
    document.body.appendChild(toast);
  }
  toast.innerHTML = `<span class="helper-grant-icon">${icon}</span><span class="helper-grant-text">+1 ${name}</span>`;
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  window.setTimeout(() => toast?.classList.remove('is-visible'), 2600);
}

async function bootstrap(): Promise<void> {
  const manager = new ScreenManager();

  // 進行状態(localStorage で永続化)
  const stageOrder = StageLoader.listStageIds(); // 例: ['1-01', '1-02', ...]
  let currentStageId = loadCurrentStageId();
  if (!stageOrder.includes(currentStageId)) currentStageId = FIRST_STAGE_ID;
  const clearedStages = loadClearedStages();
  let colorBalance = loadColorBalance();
  const heartsStore = new HeartsStore();
  const inventoryStore = new InventoryStore();
  const boosterStore = new BoosterStore();
  const characterStore = new CharacterStore();
  const paintingFragmentStore = new PaintingFragmentStore();
  const streakStore = new StreakStore();
  const dailyQuestStore = new DailyQuestStore();
  const dailyChallengeStore = new DailyChallengeStore(stageOrder);
  const achievementStore = new AchievementStore();
  const settingsStore = new SettingsStore();
  // 完成名画ギャラリーモーダル(クリア済 mask ステージ ID Set を返す)
  const paintingGalleryScreen = new PaintingGalleryScreen({
    getClearedStages: () => new Set(clearedStages),
    onClose: () => { /* モーダル閉じるだけ */ },
  });
  const adOverlay = new AdMockOverlay(document.body);
  // デイリークエスト「ブースターなしクリア」判定用:HomeScreen.onPlay で更新
  let lastBoosterUsed = false;

  // === SoundManager (Audio Tier 1) ===
  const soundManager = new SoundManager();
  window.__ck_sfx = soundManager;
  // SettingsStore と SoundManager の連携:起動時の muted 状態反映 + 設定変更購読
  soundManager.setMuted(settingsStore.get().muted);
  // SettingsStore でモーション軽減 ON なら body にクラス付与(CSS で *.cmf-reduced-motion で抑止)
  if (settingsStore.get().reducedMotion) {
    document.body.classList.add('cmf-reduced-motion');
  }
  settingsStore.subscribe((s) => {
    soundManager.setMuted(s.muted);
  });
  // Browsers block autoplay — unlock BGM on first user interaction
  document.addEventListener('pointerdown', () => {
    if (!soundManager.isBgmPlaying() && !settingsStore.get().muted) {
      soundManager.playBgm('home');
    }
  }, { once: true });

  // === Daily Login Bonus(初回 / 日替わり 1 回 +30 Color)===
  let dailyBonusGivenThisLaunch = false;
  try {
    const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
    if (window.localStorage.getItem(LS_LAST_LOGIN) !== today) {
      window.localStorage.setItem(LS_LAST_LOGIN, today);
      colorBalance += DAILY_LOGIN_BONUS;
      saveColorBalance(colorBalance);
      dailyBonusGivenThisLaunch = true;
      console.info(`[Canvas Kingdom] Daily Login Bonus: +${DAILY_LOGIN_BONUS} Color → ${colorBalance}`);
    }
  } catch {
    /* localStorage 不能環境はスキップ */
  }

  // HomeScreen は先に作って参照保持(GameplayScreen の onStageClear から呼ぶため)
  let home: HomeScreen;
  const pushHomeState = (): void => {
    if (!home) return;
    home.setColorBalance(colorBalance);
    home.setHearts(heartsStore.count, heartsStore.max, heartsStore.msToNext);
    try {
      const stage = StageLoader.load(currentStageId);
      home.setStageInfo({
        stageId: stage.stage_id,
        title: stage.metadata.title,
        targetCoverage: stage.victory_conditions.primary.value,
      });
    } catch (err) {
      console.error('[Canvas Kingdom] failed to push stage info to home:', err);
    }
    // Stage select 一覧も同期
    try {
      const list = stageOrder.map((id, idx) => {
        const s = StageLoader.load(id);
        // ロックは「直前のステージが未クリアかつ自分が cleared でも current でもない」
        const isCurrent = id === currentStageId;
        const isCleared = clearedStages.has(id);
        const prevId = idx > 0 ? stageOrder[idx - 1] : undefined;
        const prevCleared = prevId ? clearedStages.has(prevId) : true;
        const isLocked = !isCurrent && !isCleared && !prevCleared;
        return {
          stageId: s.stage_id,
          title: s.metadata.title,
          targetCoverage: s.victory_conditions.primary.value,
          isCleared,
          isCurrent,
          isLocked,
        };
      });
      home.setStages(list);
    } catch (err) {
      console.error('[Canvas Kingdom] failed to push stage list to home:', err);
    }
  };

  /** Color 残高を増減する単一の真実の源(SSOT)。
   *  ★ 修正 1:旧実装は `Math.max(0, ...)` で「正の値だけ加算」していたため、
   *     ショップでブースター購入(addColor(-250))しても Color が減らないバグがあった。
   *  ★ 修正 2:残高変動時にホーム + ショップを同期更新(ショップが開いていれば即反映)。 */
  const addColor = (amount: number): void => {
    const delta = Math.floor(amount);
    colorBalance = Math.max(0, colorBalance + delta);
    saveColorBalance(colorBalance);
    pushHomeState();
    // ShopScreen が開いていれば残高表示を即時更新
    shopScreen?.refreshBalanceIfOpen();
  };

  // ShopScreen(モーダル) — document.body に直接マウント
  const shopScreen = new ShopScreen({
    getColorBalance: () => colorBalance,
    addColor,
    inventoryStore,
    boosterStore,
    onClose: () => { /* モーダルを閉じるだけ */ },
    onWatchAd: async () => {
      const ok = await adOverlay.show({ rewardLabel: '+500 🪙 + ヘルパー×1' });
      if (ok) console.info('[Canvas Kingdom] Shop Ad watched');
      return ok;
    },
  });

  // CharacterScreen(モーダル) — document.body に直接マウント
  const characterScreen = new CharacterScreen({
    characterStore,
    getColorBalance: () => colorBalance,
    addColor,
    onClose: () => { /* モーダルを閉じるだけ */ },
    onSelect: (id) => {
      console.info(`[Canvas Kingdom] Character selected: ${CHARACTER_META[id].name}`);
      // 次のステージ開始時に反映(GameplayScreen.start() が getCharacterMeta を呼ぶ)
    },
    onWatchAd: async () => {
      const ok = await adOverlay.show({ rewardLabel: '🖌️ 絵筆解放' });
      if (ok) console.info('[Canvas Kingdom] Character Ad watched');
      return ok;
    },
  });

  const gameplay = new GameplayScreen(manager.getRoot('gameplay'), {
    inventoryStore,
    paintingFragmentStore,
    getCharacterMeta: () => characterStore.getSelectedMeta(),
    onBack: () => {
      manager.show('home');
      pushHomeState();
    },
    getColorBalance: () => colorBalance,
    getHearts: () => heartsStore.count,
    onContinueWithAd: async () => {
      const ok = await adOverlay.show({ rewardLabel: 'Continue Stage' });
      if (ok) console.info('[Canvas Kingdom] Continue: Ad watched');
      return ok;
    },
    onContinueWithHeart: () => {
      const ok = heartsStore.consume();
      if (ok) {
        console.info(`[Canvas Kingdom] Continue: −1 ❤️ → ${heartsStore.count}`);
        pushHomeState();
      }
      return ok;
    },
    onContinueWithColor: () => {
      if (colorBalance < CONTINUE_COLOR_COST) return false;
      addColor(-CONTINUE_COLOR_COST);
      console.info(`[Canvas Kingdom] Continue: −${CONTINUE_COLOR_COST} 🪙 → ${colorBalance}`);
      return true;
    },
    onCollectibleGained: (type, value) => {
      if (type === 'color_bonus') {
        const gain = value > 0 ? value : 15;
        addColor(gain);
        console.info(`[Canvas Kingdom] Collectible: +${gain} 🪙 → ${colorBalance}`);
      } else if (type === 'heart_bonus') {
        heartsStore.addOne();
        console.info(`[Canvas Kingdom] Collectible: +1 ❤️ → ${heartsStore.count}`);
      }
      // Home の Hearts チップは次回 home に戻った時に更新される
    },
    // Lot 1: ループ閉鎖時のトレイル長ボーナス。面積に応じて Color 倍率報酬。
    onLoopBonus: (cells, multiplier) => {
      // 報酬式:囲んだセル数の 30%(端数切り捨て)× 倍率
      const reward = Math.floor(cells * 0.3 * multiplier);
      if (reward > 0) {
        addColor(reward);
        console.info(
          `[Canvas Kingdom] Loop bonus: ${cells} cells × ${multiplier} → +${reward} 🪙 → ${colorBalance}`,
        );
      }
    },
    onResetProgress: () => {
      // localStorage 全消去 → 状態を初期化 → Home に戻して反映
      try {
        window.localStorage.removeItem(LS_CURRENT_STAGE);
        window.localStorage.removeItem(LS_CLEARED_STAGES);
        window.localStorage.removeItem(LS_COLOR_BALANCE);
      } catch {
        /* noop */
      }
      currentStageId = FIRST_STAGE_ID;
      clearedStages.clear();
      colorBalance = INITIAL_COLOR_BALANCE;
      heartsStore.refillFull();
      console.info('[Canvas Kingdom] Progress reset to 1-01 (Hearts refilled)');
      void (async () => {
        await gameplay.stop();
        manager.show('home');
        pushHomeState();
      })();
    },
    onStageClear: (stageId, finalCoverage) => {
      // クリア記録
      clearedStages.add(stageId);
      saveClearedStages(clearedStages);
      // デイリークエスト進捗(Retention R1)
      try {
        dailyQuestStore.onStageCleared({
          stageId,
          coverage: finalCoverage,
          usedBoosters: lastBoosterUsed,
        });
      } catch (err) {
        console.error('[Canvas Kingdom] failed to update daily quest:', err);
      }
      // Daily Challenge:今日のチャレンジステージなら自己ベスト更新
      try {
        const today = dailyChallengeStore.getToday();
        if (today.stageId === stageId) {
          dailyChallengeStore.recordAttempt(finalCoverage);
        }
      } catch (err) {
        console.error('[Canvas Kingdom] failed to update daily challenge:', err);
      }
      // Achievement:ステージクリアイベントを記録 → 新規解放があればトースト通知
      try {
        const stage = StageLoader.load(stageId);
        const isMaskStage = stage.victory_conditions.primary.type === 'composition_mask';
        // ハート/星/花 マスクのうち何個クリア済か
        const maskIds = ['2-15', '2-16', '2-17'];
        const maskStagesCleared = maskIds.filter((id) => clearedStages.has(id)).length;
        const newly = achievementStore.recordStageCleared({
          stageId,
          coverage: finalCoverage,
          usedBooster: lastBoosterUsed,
          usedHelper: false, // ステージクリア時のヘルパー使用判定は未実装、暫定 false
          clearedStagesCount: clearedStages.size,
          isMaskStage,
          maskStagesCleared,
          charactersUnlockedCount: 0, // TODO: characterStore から取得
        });
        for (const id of newly) {
          const meta = ACHIEVEMENT_META[id];
          showHelperGrantToast(meta.icon, `実績解放:${meta.title}`);
        }
      } catch (err) {
        console.error('[Canvas Kingdom] failed to record achievement:', err);
      }
      // 報酬付与(stage.rewards.color_base) — addColor 経由で UI 同期
      try {
        const cleared = StageLoader.load(stageId);
        addColor(cleared.rewards.color_base);
      } catch (err) {
        console.error('[Canvas Kingdom] failed to add color reward:', err);
      }
      // お助けアイテム 1 個ランダム付与(Skill Honored:ステージクリアで補充)
      try {
        const granted = inventoryStore.grantStageClearRandom();
        const meta = HELPER_META[granted];
        console.info(`[Canvas Kingdom] Helper granted: ${meta.icon} ${meta.name}`);
        // 短い toast 通知
        showHelperGrantToast(meta.icon, meta.name);
      } catch (err) {
        console.error('[Canvas Kingdom] failed to grant helper:', err);
      }
      // ブースター 1 個ランダム付与(Skill Honored:ステージクリアで補充)
      try {
        const grantedBooster = boosterStore.grantStageClearRandom();
        const bmeta = BOOSTER_META[grantedBooster];
        console.info(`[Canvas Kingdom] Booster granted: ${bmeta.icon} ${bmeta.name}`);
        showHelperGrantToast(bmeta.icon, bmeta.name);
      } catch (err) {
        console.error('[Canvas Kingdom] failed to grant booster:', err);
      }
      // キャラクターアンロック(ステージクリア条件)
      try {
        const newlyUnlocked = characterStore.unlockByStageClear(stageId);
        for (const id of newlyUnlocked) {
          const cmeta = CHARACTER_META[id];
          console.info(`[Canvas Kingdom] Character unlocked: ${cmeta.name}`);
          showHelperGrantToast('🖌️', `${cmeta.name} 解放!`);
        }
      } catch (err) {
        console.error('[Canvas Kingdom] failed to unlock character:', err);
      }
      // 次ステージへ進行
      const next = nextStageId(stageId, stageOrder);
      currentStageId = next;
      saveCurrentStageId(next);
      console.info(
        `[Canvas Kingdom] Stage cleared: ${stageId} (coverage ${(finalCoverage * 100).toFixed(1)}%) → next: ${next} | color: ${colorBalance}`,
      );
      pushHomeState();
      // 同 GameplayScreen を一度 stop → 次ステージで再 start
      void (async () => {
        await gameplay.stop();
        await gameplay.start(next);
      })();
    },
  });

  home = new HomeScreen(manager.getRoot('home'), {
    streakStore,
    dailyQuestStore,
    dailyChallengeStore,
    onOpenGallery: () => paintingGalleryScreen.open(),
    onPlayDailyChallenge: (stageId) => {
      // Daily Challenge:指定ステージで即プレイ。クリア時の recordAttempt は onStageClear 側で対応
      currentStageId = stageId;
      saveCurrentStageId(stageId);
      lastBoosterUsed = false;
      manager.show('gameplay');
      gameplay
        .start(stageId, { brushBoost: false, bigStart: false, extraLife: false })
        .catch((err: unknown) => {
          console.error('[GameplayScreen.start daily-challenge] failed:', err);
        });
    },
    isChapter1Complete: () => {
      // 1-01 〜 1-40 が全てクリア済みかチェック
      for (let i = 1; i <= 40; i++) {
        const id = `1-${i.toString().padStart(2, '0')}`;
        if (!clearedStages.has(id)) return false;
      }
      return true;
    },
    onPlay: (boosters) => {
      // 残数方式: BoosterStore.consume() は HomeScreen 内で実施済み、Color 差引き不要
      lastBoosterUsed = boosters.brushBoost || boosters.bigStart || boosters.extraLife;
      console.info(
        `[Canvas Kingdom] Boosters used (InventoryStore) — brushBoost: ${boosters.brushBoost}, bigStart: ${boosters.bigStart}, extraLife: ${boosters.extraLife}`,
      );
      manager.show('gameplay');
      gameplay
        .start(currentStageId, {
          brushBoost: boosters.brushBoost,
          bigStart: boosters.bigStart,
          extraLife: boosters.extraLife,
        })
        .catch((err: unknown) => {
          console.error('[GameplayScreen.start] failed:', err);
        });
    },
    onSelectStage: (stageId) => {
      if (!stageOrder.includes(stageId)) return;
      currentStageId = stageId;
      saveCurrentStageId(stageId);
      pushHomeState();
    },
    getColorBalance: () => colorBalance,
    onOpenShop: () => shopScreen.open(),
    onOpenCharacter: () => characterScreen.open(),
    boosterStore,
    getPaintingProgress: () => ({
      current: paintingFragmentStore.count(1),
      max: paintingFragmentStore.fragmentsPerPainting(),
    }),
  });
  // 初期表示を反映
  pushHomeState();

  // Daily Login Bonus はもらった場合のみ Home でトースト表示
  if (dailyBonusGivenThisLaunch) {
    window.setTimeout(() => {
      home.showToast(`🎁 Daily Bonus  +${DAILY_LOGIN_BONUS} Color`, 2400);
    }, 700);
  }

  new TopScreen(manager.getRoot('top'), {
    onStart: () => manager.show('home'),
  });

  manager.show('top');

  // M3: ブラウザ/モバイルバックキー対応。
  // popstate 起因の show は showWithoutHistory() で履歴を二重に積まない。
  // gameplay からバックした場合は onBack と同じ処理(stop + home)を実行する。
  window.addEventListener('popstate', (e: PopStateEvent) => {
    const state = e.state as { screen?: string } | null;
    const screen = state?.screen;
    if (screen !== 'top' && screen !== 'home' && screen !== 'gameplay') return;
    const targetId = screen as import('./ui/theme').ScreenId;
    if (targetId === 'home' && manager.getCurrent() === 'gameplay') {
      void gameplay.stop().then(() => {
        manager.showWithoutHistory('home');
        pushHomeState();
      });
    } else {
      manager.showWithoutHistory(targetId);
    }
  });

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      void gameplay.stop();
    });
  }

  console.info('[Canvas Kingdom] Phase A bootstrap complete', {
    screens: ['top', 'home', 'gameplay'],
    initial: 'top',
    currentStageId,
    clearedCount: clearedStages.size,
    stageOrder,
  });
}

bootstrap().catch((error: unknown) => {
  console.error('[Canvas Kingdom] Bootstrap failed:', error);
});
